/**
 * EventCalendar — the Event Calendar block on the Event Hub landing page.
 *
 * Presentational: the page fetches the events and passes them in. It used to fetch
 * through a client prop of its own, which duplicated the page's request — `ListEvents`
 * has no filter parameters, so both were asking for the same whole set.
 *
 * Layout follows the Figma's header row — title, search, Month / Table View toggle, then
 * the caller's primary action — followed by the filter bar, the legend and the month grid.
 * That action differs by audience: a marketer gets Create New Event, a BD gets Nominate,
 * because a BD cannot create events and a marketer cannot nominate.
 *
 * A data-driven month grid, Monday-first, laid out a week at a time. As the Figma draws it, a
 * multi-day event is ONE bar across the days it covers with its name once, and a single-day event
 * a chip in its day. Each week row is a 7-column CSS grid: the day cells span every row of it, and
 * the bars sit over them in lanes, cut at the week's edges (square on the cut side) and packed so a
 * long event keeps its row. Every row is shown — a busy week is taller — rather than folded into
 * "+N more". The packing is utils/calendarLayout; no layout library is needed.
 *
 * Table View is the event list — there is no separate list page. It shows the same
 * filtered events as rows, through the shared EventTable. The Figma draws the toggle but
 * has no frame behind it, so those columns are a judgement call (see EventTable). It
 * earns its place regardless: an event with no `startDate` has no cell on the grid, so
 * the table is the only view that can show one.
 *
 * The view toggle and the filter bar are CONTROLLABLE. Left uncontrolled they keep their
 * own state, which is all a standalone caller needs; the landing page controls both from
 * the query string so that "1 Events to Publish" can switch this to the table and select
 * Status = Draft in one click. Every such control is optional, so neither host pays for
 * the other's needs.
 *
 * `bdView` is the BD's calendar: no Status filter or column (a BD sees published events
 * only, so Status says nothing), and only events for the BD's own BUs — the grid, the table and
 * the BU filter's options alike. An event with no BUs is for no BD, so it is not shown
 * (eventIsForBusinessUnits, the same rule nominating uses). The BU filter then lists the BD's
 * BUs under "All my BUs", which means any of them — there is no way to widen to every event.
 *
 * Child events — events directly under another event in the campaign hierarchy — are hidden by
 * default, so a programme shows as its parent rather than as the parent plus every session; a
 * "Show child events" toggle brings them back. The host works out which events are children
 * (hierarchySync `childEventIds`) and passes them in; while it does not know, nothing is hidden and
 * the toggle is not shown — hiding is a convenience, so not knowing must not lose events.
 */

import Box from "@amzn/awsui-components-react/polaris/box";
import Button from "@amzn/awsui-components-react/polaris/button";
import Header from "@amzn/awsui-components-react/polaris/header";
import Input from "@amzn/awsui-components-react/polaris/input";
import SegmentedControl from "@amzn/awsui-components-react/polaris/segmented-control";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Toggle from "@amzn/awsui-components-react/polaris/toggle";
import { useMemo, useState } from "react";
import type { Event } from "../../client/EventCalendarClient";
import {
  ALL,
  EMPTY_FILTERS,
  EventFilters,
  filterEvents,
  isEmptyFilter,
  type EventFilterState,
} from "../EventFilters";
import { EventTable } from "../EventTable";
import { eventIsForBusinessUnits } from "../../utils/nominatableEvents";
import { NO_TYPE_CLASS, TYPE_ORDER, typeMeta, typeMetaOf } from "../../utils/eventTaxonomy";
import { dayIndex, layoutWeek, type Segment } from "../../utils/calendarLayout";
import "./EventCalendar.css";
import { EventTypeIcon } from "../EventTypeIcon";

export interface EventCalendarProps {
  /** Every event, fetched by the page. Filtering and month selection happen here. */
  events: readonly Event[];
  /** True while the page's fetch is in flight; the grid dims but stays mounted. */
  loading?: boolean;
  /**
   * The caller's BUs when they nominate (useNominationBusinessUnits): the Nomination filter's
   * "open" then means open to them. Undefined for everyone else.
   */
  bdBusinessUnits?: readonly string[];
  /**
   * The BD's calendar (see the header): hides Status, and shows only events for
   * {@link bdBusinessUnits} — none while those are loading or failed to load, as `[]` matches
   * nothing. A view decision, so the host passes its audience, not a role.
   */
  bdView?: boolean;
  /**
   * Called when a grid chip is clicked. Event detail is a page, so navigating there is
   * the host's business — this component never touches routing, which keeps it
   * independent of the router.
   */
  onSelectEvent?: (event: Event) => void;
  /**
   * Renders the Create New Event button when provided. Creating is the host's business
   * too (it owns the dialog and where the user lands afterwards); this only places the
   * button where the Figma puts it.
   */
  onCreateEvent?: () => void;
  /**
   * Renders the Nominate button when provided, in the same slot.
   *
   * Separate from {@link onCreateEvent} rather than one "primary action" prop, because
   * which button belongs there is a consequence of what the caller may DO: a marketer
   * creates events and cannot nominate, a BD nominates and cannot create. Two optional
   * props let the page pass the one it has, and make passing both an obvious mistake
   * rather than a silently overloaded parameter.
   */
  onNominate?: () => void;
  /**
   * Which view to show. Omit to let the component keep its own; pass it (with
   * {@link onViewChange}) to drive it from outside — the landing page reads it from the
   * URL so a link can land on the table.
   */
  view?: CalendarView;
  onViewChange?: (view: CalendarView) => void;
  /** Filter state. Omit to let the component own it; pass it to control it. */
  filters?: EventFilterState;
  onFiltersChange?: (filters: EventFilterState) => void;
  /**
   * The child events (see the header): hidden unless {@link showChildEvents}. Undefined while the
   * host does not know them (loading, or the read failed) — then nothing is hidden and there is no
   * toggle.
   */
  childEventIds?: ReadonlySet<string>;
  /** Whether child events show. Omit to let the component own it (default: hidden). */
  showChildEvents?: boolean;
  onShowChildEventsChange?: (show: boolean) => void;
}

export type CalendarView = "month" | "table";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// ── Date helpers (local timezone, no dependencies) ───────────────────────────

/**
 * Builds the day-cell matrix for a month (6×7 or fewer), Monday-first. The padding days
 * from the adjacent months are included and flagged so they can be greyed out.
 */
function buildMonthMatrix(year: number, month: number): { date: Date; inMonth: boolean }[] {
  const firstOfMonth = new Date(year, month, 1);
  // JS getDay(): 0=Sunday..6=Saturday. Shift so Monday=0.
  const leading = (firstOfMonth.getDay() + 6) % 7;
  const gridStart = new Date(year, month, 1 - leading);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const totalCells = Math.ceil((leading + daysInMonth) / 7) * 7;

  const cells: { date: Date; inMonth: boolean }[] = [];
  for (let i = 0; i < totalCells; i++) {
    const date = new Date(
      gridStart.getFullYear(),
      gridStart.getMonth(),
      gridStart.getDate() + i,
    );
    cells.push({ date, inMonth: date.getMonth() === month });
  }
  return cells;
}

// ── Component ────────────────────────────────────────────────────────────────

export function EventCalendar({
  events,
  loading,
  bdBusinessUnits,
  bdView = false,
  onSelectEvent,
  onCreateEvent,
  onNominate,
  view: controlledView,
  onViewChange,
  filters: controlledFilters,
  onFiltersChange,
  childEventIds,
  showChildEvents: controlledShowChildren,
  onShowChildEventsChange,
}: EventCalendarProps) {
  /* Controlled-or-internal, for both the view and the filters. The internal state is
     still declared when controlled — a hook cannot be conditional — but it simply goes
     unread, which is cheaper than two versions of this component. */
  const [internalView, setInternalView] = useState<CalendarView>("month");
  const view = controlledView ?? internalView;
  const setView = (next: CalendarView) =>
    onViewChange ? onViewChange(next) : setInternalView(next);

  const [internalFilters, setInternalFilters] = useState<EventFilterState>(EMPTY_FILTERS);
  const filters = controlledFilters ?? internalFilters;
  const setFilters = (next: EventFilterState) =>
    onFiltersChange ? onFiltersChange(next) : setInternalFilters(next);

  const [internalShowChildren, setInternalShowChildren] = useState(false);
  const showChildren = controlledShowChildren ?? internalShowChildren;
  const setShowChildren = (next: boolean) =>
    onShowChildEventsChange ? onShowChildEventsChange(next) : setInternalShowChildren(next);

  // Always open on the current month; the user pages from there.
  const [{ year, month }, setVisible] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  const matrix = useMemo(() => buildMonthMatrix(year, month), [year, month]);

  /* The BD sees only their BUs' events. Applied before the filter bar, not as its BU value, so
     every select — the city and owner options too — is drawn from what the BD can see. */
  /* Child events go the same way, before the filter bar, so its options do not offer a city or
     owner only a hidden child has. */
  const visible = useMemo(
    () =>
      events.filter(
        (e) =>
          (!bdView || eventIsForBusinessUnits(e, bdBusinessUnits ?? [])) &&
          (showChildren || !childEventIds || !e.eventId || !childEventIds.has(e.eventId)),
      ),
    [events, bdView, bdBusinessUnits, showChildren, childEventIds],
  );

  /* A hidden Status filter must not narrow either: a deep-linked `status=DRAFT` would
     otherwise empty a BD's calendar with no control on screen to undo it. */
  const filtered = useMemo(
    () =>
      filterEvents(
        visible,
        bdView ? { ...filters, status: ALL } : filters,
        new Date(),
        bdBusinessUnits,
      ),
    [visible, filters, bdBusinessUnits, bdView],
  );

  const todayIdx = dayIndex(new Date());

  /* The month as week rows. An event without a `startDate` (optional on the wire, only enforced
     at PublishEvent — the normal state of a freshly created event) has no day and is left out by
     the layout rather than defaulted to one; the table shows it. */
  const weeks = useMemo(() => {
    const rows: { date: Date; inMonth: boolean }[][] = [];
    for (let i = 0; i < matrix.length; i += 7) rows.push(matrix.slice(i, i + 7));
    return rows.map((days) => ({ days, layout: layoutWeek(dayIndex(days[0]!.date), filtered) }));
  }, [matrix, filtered]);

  /* The event under the pointer (or keyboard focus). An event cut across weeks is several bars, so
     hovering one lights them all up — CSS :hover alone would light only the one under the pointer. */
  const [hoveredId, setHoveredId] = useState<string | undefined>();

  /** One event's bar. A cut side (the event carries on into another week) is square. */
  const chip = (e: Event, seg: Segment) => (
    <button
      key={e.eventId}
      type="button"
      className={`ec-chip ${typeMetaOf(e)?.className ?? NO_TYPE_CLASS}${
        e.contentStatus === "DRAFT" ? " ec-chip--draft" : ""
      }${seg.continuesBefore ? " ec-chip--cut-start" : ""}${
        seg.continuesAfter ? " ec-chip--cut-end" : ""
      }${e.eventId !== undefined && e.eventId === hoveredId ? " ec-chip--hover" : ""} ec-chip--bar`}
      onMouseEnter={() => setHoveredId(e.eventId)}
      onMouseLeave={() => setHoveredId(undefined)}
      onFocus={() => setHoveredId(e.eventId)}
      onBlur={() => setHoveredId(undefined)}
      style={{ gridColumn: `${seg.startCol + 1} / span ${seg.span}`, gridRow: seg.lane + 2 }}
      title={e.eventName}
      onClick={() => onSelectEvent?.(e)}
    >
      {e.eventType ? (
        <EventTypeIcon type={e.eventType} size="chip" className="ec-chip__icon" />
      ) : null}
      <span className="ec-chip__label">
        {/* A draft is marketing-visible only, so say so on the chip — the Figma marks them the
            same way. */}
        {e.contentStatus === "DRAFT" ? "(Draft) " : ""}
        {e.eventName}
      </span>
    </button>
  );

  const goToMonth = (delta: number) => {
    const next = new Date(year, month + delta, 1);
    setVisible({ year: next.getFullYear(), month: next.getMonth() });
  };

  return (
    <div className="ec-root">
      <Header
        variant="h2"
        actions={
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            {childEventIds?.size ? (
              <Toggle checked={showChildren} onChange={({ detail }) => setShowChildren(detail.checked)}>
                Show child events
              </Toggle>
            ) : null}
            <div className="ec-search">
              <Input
                type="search"
                value={filters.search}
                onChange={({ detail }) =>
                  setFilters({ ...filters, search: detail.value })
                }
                placeholder="Enter value"
                ariaLabel="Search events by name"
              />
            </div>
            <SegmentedControl
              selectedId={view}
              onChange={({ detail }) => setView(detail.selectedId as CalendarView)}
              label="Calendar view"
              options={[
                { id: "month", text: "Month" },
                { id: "table", text: "Table View" },
              ]}
            />
            {/* One or the other, never both — see the note on onNominate. */}
            {/* Normal, not primary. The Figma uses its "Normal button" atom for both —
                white fill, blue border and label (#006ce0). Nothing on this page is the
                one obvious next step, so a filled button here would outrank the page's
                actual content. */}
            {onCreateEvent ? (
              <Button onClick={onCreateEvent}>Create New Event</Button>
            ) : null}
            {onNominate ? <Button onClick={onNominate}>Nominate</Button> : null}
          </SpaceBetween>
        }
      >
        Event Calendar
      </Header>

      {/* The search box lives in the header row here, where the Figma puts it, so the
          shared bar renders only the six selects. */}
      <EventFilters
        events={visible}
        value={filters}
        onChange={setFilters}
        hideStatus={bdView}
        businessUnits={bdView ? bdBusinessUnits : undefined}
      />

      <div className="ec-legend">
        {TYPE_ORDER.map((t) => (
          <span key={t} className="ec-legend-item">
            <EventTypeIcon type={t} />
            {typeMeta(t).label}
          </span>
        ))}
      </div>

      {view === "table" ? (
        <EventTable
          events={filtered}
          loading={loading}
          onSelectEvent={onSelectEvent}
          hideStatus={bdView}
          empty={
            <Box textAlign="center" padding={{ vertical: 'l' }} color="text-status-inactive">
              {isEmptyFilter(filters) ? 'No events' : 'No matching events'}
            </Box>
          }
        />
      ) : (
        <>
          {/* The grid stays mounted while loading: swapping in a spinner would collapse
              the page height and expand it again, which reads as a visual jump. */}
          <div
            className={`ec-grid${loading ? " ec-grid--loading" : ""}`}
            aria-busy={!!loading}
          >
            {/* The month bar belongs INSIDE the grid, above the weekday row, as the Figma
                draws it: one bordered card whose header names the month being shown. Its own
                buttons rather than AWSUI icon buttons, so the chevrons can be white on the
                accent fill — AWSUI's icon variant fixes its colour. */}
            <div className="ec-monthbar">
              <button
                type="button"
                className="ec-monthbar__nav"
                aria-label="Previous month"
                onClick={() => goToMonth(-1)}
              >
                ◀
              </button>
              <span className="ec-monthbar__label">
                {MONTH_NAMES[month]} {year}
              </span>
              <button
                type="button"
                className="ec-monthbar__nav"
                aria-label="Next month"
                onClick={() => goToMonth(1)}
              >
                ▶
              </button>
            </div>

            <div className="ec-weekdays">
              {WEEKDAYS.map((d) => (
                <div key={d} className="ec-weekday">
                  {d}
                </div>
              ))}
            </div>
            <div className="ec-body">
              {weeks.map(({ days, layout }) => {
                /* Row 1 holds the day numbers, then one row per lane; the last row stretches so
                   the week keeps its height when it is empty. */
                const rows = layout.lanes;
                return (
                  <div
                    key={days[0]!.date.toISOString()}
                    className="ec-week"
                    style={{ gridTemplateRows: `26px ${rows ? `repeat(${rows}, auto) ` : ""}1fr` }}
                  >
                    {days.map(({ date, inMonth }, col) => {
                      const isToday = dayIndex(date) === todayIdx;
                      return (
                        <div
                          key={date.toISOString()}
                          className={`ec-cell${inMonth ? "" : " ec-cell--muted"}${
                            isToday ? " ec-cell--today" : ""
                          }`}
                          style={{ gridColumn: col + 1 }}
                        >
                          <div className={`ec-cell-num${isToday ? " ec-cell-num--today" : ""}`}>
                            {date.getDate()}
                          </div>
                        </div>
                      );
                    })}
                    {layout.segments.map((seg) => chip(seg.event, seg))}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
